<template>
	<div class="dashboard-root">
		<v-row v-if="showBirthdaysCard">
			<v-col cols="12" md="7" lg="6">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-cake-variant</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Verjaardagen (komende 7 dagen)
						</v-toolbar-title>
					</v-toolbar>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="birthdaysError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ birthdaysError }}
						</v-alert>
						<v-data-table
							v-else
							:headers="birthdayHeaders"
							:items="birthdayRows"
							:loading="birthdaysLoading"
							item-key="employee_id"
							hide-default-footer
							disable-pagination
							dense
							class="dashboard-data-table elevation-0"
							no-data-text="Geen verjaardagen van actieve werknemers in de komende 7 dagen."
						/>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-if="showWeekDashboardSection" align="center" class="dashboard-week-filter-row">
			<v-col cols="12">
				<v-card class="dashboard-week-filter-card pa-3" outlined elevation="1">
					<div class="d-flex flex-wrap align-center">
						<span class="body-2 font-weight-medium mr-3 mb-2 mb-sm-0">
							{{ weekFilterBannerText }}
						</span>
						<div class="d-flex flex-wrap align-center">
							<v-btn icon small class="mr-1" aria-label="Vorige week" @click="shiftSelectedWeek(-7)">
								<v-icon>mdi-chevron-left</v-icon>
							</v-btn>
							<v-menu
								v-model="menuWeekPicker"
								:close-on-content-click="false"
								transition="scale-transition"
								offset-y
								min-width="290px"
							>
								<template v-slot:activator="{ on }">
									<v-btn depressed color="primary" outlined class="mr-1" v-on="on">
										<v-icon left small>mdi-calendar</v-icon>
										{{ selectedWeekRangeLabel }}
									</v-btn>
								</template>
								<v-date-picker
									v-model="selectedWeekAnyDay"
									no-title
									scrollable
									locale="nl-nl"
									:first-day-of-week="1"
									@input="onWeekPickerInput"
								/>
							</v-menu>
							<v-btn icon small class="mr-2" aria-label="Volgende week" @click="shiftSelectedWeek(7)">
								<v-icon>mdi-chevron-right</v-icon>
							</v-btn>
							<v-btn text small color="primary" @click="resetWeekToToday">
								Deze week
							</v-btn>
						</div>
					</div>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-if="showWeekDashboardSection">
			<v-col cols="12" md="6" lg="6">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-calendar-week</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Docentenverzuim
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="selectedWeekRangeLabel" class="py-2 pb-0 caption text--secondary">
						{{ selectedWeekRangeLabel }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="teacherAbsentError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ teacherAbsentError }}
						</v-alert>
						<v-data-table
							v-else
							:headers="teacherAbsentHeaders"
							:items="teacherAbsentRows"
							:loading="teacherAbsentLoading"
							item-key="id"
							hide-default-footer
							disable-pagination
							dense
							class="dashboard-data-table elevation-0"
							no-data-text="Geen docentenverzuim met start of einddatum in de geselecteerde week."
						/>
					</v-card-text>
				</v-card>
			</v-col>

			<v-col cols="12" md="6" lg="6">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-chart-bar</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Geannuleerde modules per sector
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="selectedWeekRangeLabel" class="py-2 pb-0 caption text--secondary">
						{{ selectedWeekRangeLabel }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="sectorAbsentError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ sectorAbsentError }}
						</v-alert>
						<v-data-table
							v-else
							:headers="sectorAbsentHeaders"
							:items="sectorAbsentRows"
							:loading="sectorAbsentLoading"
							item-key="sector_key"
							hide-default-footer
							disable-pagination
							dense
							class="dashboard-data-table elevation-0"
							no-data-text="Geen geannuleerde docent-modules in de geselecteerde week."
						>
							<template v-slot:item.bar="{ item }">
								<div class="sector-bar-wrap">
									<v-progress-linear
										:value="sectorBarPercent(item.count)"
										color="primary"
										height="8"
										rounded
									/>
								</div>
							</template>
						</v-data-table>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-if="showEmployeeAbsentCard" class="mt-2">
			<v-col cols="12" md="7" lg="6">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-account-off-outline</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Verzuim medewerkers (N-O-P)
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="selectedWeekRangeLabel" class="py-2 pb-0 caption text--secondary">
						{{ selectedWeekRangeLabel }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="employeeAbsentError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ employeeAbsentError }}
						</v-alert>
						<v-data-table
							v-else
							:headers="employeeAbsentHeaders"
							:items="employeeAbsentRows"
							:loading="employeeAbsentLoading"
							item-key="id"
							hide-default-footer
							disable-pagination
							dense
							class="dashboard-data-table elevation-0"
							no-data-text="Geen medewerkersverzuim met start of einddatum in de geselecteerde week."
						/>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../config";
import {
	isDashboardBlockedRole,
	isDashboardCoordinatorRole,
	isDashboardFullRole,
	effectiveDashboardRoleId,
	dashboardCoordinatorIdentity,
	dashboardBlockedRedirectPath
} from "../router/authMeta";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "dashboard_operations",
	data() {
		return {
			dashboardUser: null,
			menuWeekPicker: false,
			selectedWeekAnyDay: "",
			birthdays: [],
			birthdaysLoading: false,
			birthdaysError: null,
			teacherAbsences: [],
			teacherAbsentLoading: false,
			teacherAbsentError: null,
			employeeAbsences: [],
			employeeAbsentLoading: false,
			employeeAbsentError: null,
			sectorAbsentCounts: [],
			sectorAbsentLoading: false,
			sectorAbsentError: null,
			birthdayHeaders: [
				{ text: "Naam", value: "name", sortable: true },
				{ text: "Functie", value: "function_name", sortable: true },
				{ text: "Verjaardag", value: "birthday_display", sortable: true }
			],
			teacherAbsentHeaders: [
				{ text: "Docent", value: "teacher_name", sortable: true },
				{ text: "Start", value: "absent_start_display", sortable: true },
				{ text: "Einde", value: "absent_end_display", sortable: true }
			],
			employeeAbsentHeaders: [
				{ text: "Medewerker", value: "employee_name", sortable: true },
				{ text: "Start", value: "absent_start_display", sortable: true },
				{ text: "Einde", value: "absent_end_display", sortable: true }
			],
			sectorAbsentHeaders: [
				{ text: "Sector", value: "sector_name", sortable: true },
				{ text: "Aantal", value: "count", sortable: true, width: "88px" },
				{ text: "", value: "bar", sortable: false, width: "45%" }
			]
		};
	},
	computed: {
		dashboardRoleId() {
			return effectiveDashboardRoleId(this.dashboardUser);
		},
		showBirthdaysCard() {
			return isDashboardFullRole(this.dashboardRoleId);
		},
		showEmployeeAbsentCard() {
			return isDashboardFullRole(this.dashboardRoleId);
		},
		showWeekDashboardSection() {
			return (
				isDashboardFullRole(this.dashboardRoleId) ||
				isDashboardCoordinatorRole(this.dashboardRoleId)
			);
		},
		weekFilterBannerText() {
			if (isDashboardCoordinatorRole(this.dashboardRoleId)) {
				return "Week (docentverzuim & geannuleerde modules — uw opleidingen)";
			}
			return "Week (docent-, medewerkersverzuim & sectoren)";
		},
		birthdayRows() {
			var self = this;
			return (this.birthdays || []).map(function(b) {
				return {
					employee_id: b.employee_id,
					name: self.formatEmployeeName(b),
					function_name: b.function_name || "—",
					birthday_display: self.formatBirthdayWithWeekday(b.next_birthday)
				};
			});
		},
		selectedWeekRangeLabel() {
			return this.formatWeekRangeLabelForYmd(this.selectedWeekAnyDay);
		},
		teacherAbsentRows() {
			var self = this;
			return (this.teacherAbsences || []).map(function(row) {
				return {
					id: row.id,
					teacher_name: row.teacher_name || "—",
					absent_start_display: self.formatShortDate(row.absent_start),
					absent_end_display: self.formatShortDate(row.absent_end)
				};
			});
		},
		employeeAbsentRows() {
			var self = this;
			return (this.employeeAbsences || []).map(function(row) {
				return {
					id: row.id,
					employee_name: row.employee_name || "—",
					absent_start_display: self.formatShortDate(row.absent_start),
					absent_end_display: self.formatShortDate(row.absent_end)
				};
			});
		},
		sectorAbsentRows() {
			return (this.sectorAbsentCounts || []).map(function(row) {
				return {
					sector_key: row.sector_id != null ? "s" + row.sector_id : "_none",
					sector_name: row.sector_name || "—",
					count: row.count,
					bar: row.count
				};
			});
		},
		maxSectorAbsentCount() {
			var rows = this.sectorAbsentCounts || [];
			if (!rows.length) {
				return 0;
			}
			return Math.max.apply(
				null,
				rows.map(function(r) {
					return r.count || 0;
				})
			);
		}
	},
	created() {
		try {
			this.dashboardUser = JSON.parse(localStorage.getItem("user") || "null");
		} catch (e) {
			this.dashboardUser = null;
		}
		var rid = this.dashboardRoleId;
		if (isDashboardBlockedRole(rid)) {
			this.$router.replace({ path: dashboardBlockedRedirectPath(rid), replace: true });
			return;
		}
		this.selectedWeekAnyDay = this.toYmdLocal(new Date());
		this.loadOperationsWidgets();
	},
	methods: {
		weekQueryParams() {
			var p = {};
			if (this.selectedWeekAnyDay) {
				p.week = this.selectedWeekAnyDay;
			}
			if (this.dashboardRoleId != null && !Number.isNaN(this.dashboardRoleId)) {
				p.role_id = this.dashboardRoleId;
			}
			var un = dashboardCoordinatorIdentity(this.dashboardUser);
			if (un) {
				p.username = un;
			}
			return p;
		},
		loadOperationsWidgets() {
			if (this.showBirthdaysCard) {
				this.loadUpcomingBirthdays();
			}
			if (this.showWeekDashboardSection) {
				this.loadTeacherAbsentWeek();
				this.loadSectorAbsentWeek();
			}
			if (this.showEmployeeAbsentCard) {
				this.loadEmployeeAbsentWeek();
			}
		},
		reloadWeekWidgets() {
			if (!this.showWeekDashboardSection) {
				return;
			}
			this.loadTeacherAbsentWeek();
			this.loadSectorAbsentWeek();
		},
		loadUpcomingBirthdays() {
			var self = this;
			this.birthdaysLoading = true;
			this.birthdaysError = null;
			ax.get("/dashboard/upcoming_birthdays", { params: self.weekQueryParams() })
				.then(function(response) {
					self.birthdays = response.data || [];
					self.birthdaysLoading = false;
				})
				.catch(function(err) {
					self.birthdaysLoading = false;
					self.birthdaysError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon verjaardagen niet laden.";
				});
		},
		loadTeacherAbsentWeek() {
			var self = this;
			this.teacherAbsentLoading = true;
			this.teacherAbsentError = null;
			ax.get("/dashboard/teacher_absent_current_week", { params: self.weekQueryParams() })
				.then(function(response) {
					self.teacherAbsences = response.data || [];
					self.teacherAbsentLoading = false;
				})
				.catch(function(err) {
					self.teacherAbsentLoading = false;
					self.teacherAbsentError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon docentenverzuim niet laden.";
				});
		},
		loadEmployeeAbsentWeek() {
			var self = this;
			this.employeeAbsentLoading = true;
			this.employeeAbsentError = null;
			ax.get("/dashboard/employee_absent_week", { params: self.weekQueryParams() })
				.then(function(response) {
					self.employeeAbsences = response.data || [];
					self.employeeAbsentLoading = false;
				})
				.catch(function(err) {
					self.employeeAbsentLoading = false;
					self.employeeAbsentError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon medewerkersverzuim niet laden.";
				});
		},
		loadSectorAbsentWeek() {
			var self = this;
			this.sectorAbsentLoading = true;
			this.sectorAbsentError = null;
			ax.get("/dashboard/cancelled_classes_sector_current_week", { params: self.weekQueryParams() })
				.then(function(response) {
					self.sectorAbsentCounts = response.data || [];
					self.sectorAbsentLoading = false;
				})
				.catch(function(err) {
					self.sectorAbsentLoading = false;
					self.sectorAbsentError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon sectoroverzicht niet laden.";
				});
		},
		onWeekPickerInput() {
			this.menuWeekPicker = false;
			this.reloadWeekWidgets();
		},
		shiftSelectedWeek(deltaDays) {
			var d = this.parseYmdToLocalDate(this.selectedWeekAnyDay) || new Date();
			d.setDate(d.getDate() + deltaDays);
			this.selectedWeekAnyDay = this.toYmdLocal(d);
			this.reloadWeekWidgets();
		},
		resetWeekToToday() {
			this.selectedWeekAnyDay = this.toYmdLocal(new Date());
			this.reloadWeekWidgets();
		},
		toYmdLocal(d) {
			var y = d.getFullYear();
			var m = String(d.getMonth() + 1).padStart(2, "0");
			var day = String(d.getDate()).padStart(2, "0");
			return y + "-" + m + "-" + day;
		},
		parseYmdToLocalDate(s) {
			if (!s) {
				return null;
			}
			var parts = String(s)
				.slice(0, 10)
				.split("-");
			if (parts.length !== 3) {
				return null;
			}
			var d = new Date(
				parseInt(parts[0], 10),
				parseInt(parts[1], 10) - 1,
				parseInt(parts[2], 10)
			);
			return Number.isNaN(d.getTime()) ? null : d;
		},
		startOfWeekMonday(ref) {
			var x = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
			var dow = x.getDay();
			var offset = dow === 0 ? -6 : 1 - dow;
			x.setDate(x.getDate() + offset);
			return x;
		},
		formatWeekRangeLabelForYmd(ymd) {
			var ref = this.parseYmdToLocalDate(ymd);
			if (!ref) {
				ref = new Date();
			}
			var ws = this.startOfWeekMonday(ref);
			var we = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() + 6);
			var opts = { day: "numeric", month: "short", year: "numeric" };
			return (
				"Week: " +
				ws.toLocaleDateString("nl-NL", opts) +
				" – " +
				we.toLocaleDateString("nl-NL", opts) +
				" (ma–zo)"
			);
		},
		sectorBarPercent(count) {
			var max = this.maxSectorAbsentCount;
			if (!max || !count) {
				return 0;
			}
			return Math.round((count / max) * 100);
		},
		formatShortDate(ymd) {
			if (ymd == null || ymd === "") {
				return "—";
			}
			var parts = String(ymd).slice(0, 10).split("-");
			if (parts.length !== 3) {
				return String(ymd);
			}
			var d = new Date(
				parseInt(parts[0], 10),
				parseInt(parts[1], 10) - 1,
				parseInt(parts[2], 10)
			);
			if (Number.isNaN(d.getTime())) {
				return String(ymd);
			}
			return d.toLocaleDateString("nl-NL", {
				day: "numeric",
				month: "short",
				year: "numeric"
			});
		},
		formatEmployeeName(row) {
			if (row.display_name && String(row.display_name).trim()) {
				return row.display_name.trim();
			}
			var parts = [row.firstname, row.maidenname, row.surname].filter(function(p) {
				return p && String(p).trim();
			});
			return parts.join(" ") || "—";
		},
		formatBirthdayWithWeekday(value) {
			if (value == null || value === "") {
				return "—";
			}
			var s = String(value).slice(0, 10);
			var parts = s.split("-");
			var d;
			if (parts.length === 3) {
				d = new Date(
					parseInt(parts[0], 10),
					parseInt(parts[1], 10) - 1,
					parseInt(parts[2], 10)
				);
			} else {
				d = new Date(value);
			}
			if (Number.isNaN(d.getTime())) {
				return String(value);
			}
			return d.toLocaleDateString("nl-NL", {
				weekday: "long",
				day: "numeric",
				month: "long",
				year: "numeric"
			});
		}
	}
};
</script>

<style scoped>
.dashboard-root {
	width: 100%;
}

.dashboard-widget-card {
	border-radius: 4px;
	overflow: hidden;
}

.dashboard-widget-toolbar {
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}

.dashboard-week-filter-row {
	margin-top: 4px;
}

.dashboard-week-filter-card {
	border-radius: 4px;
}
</style>

<style>
.sector-bar-wrap {
	min-width: 120px;
	padding: 4px 0;
}

.dashboard-data-table.v-data-table thead tr th {
	background-color: rgba(245, 124, 0, 0.1) !important;
	border-bottom: thin solid rgba(245, 124, 0, 0.35) !important;
	font-weight: 600 !important;
	font-size: 0.8125rem !important;
}
</style>
